<script setup lang="ts">
import { computed, ref } from 'vue';
import type { ConnectionTool } from '../model.js';
import type { DirectorySort, DirectoryView } from '../lib/customization.js';
import { groupDevices, matchesQuery, sortDevices, type DirectoryDevice } from '../lib/directory.js';
import { pluralize } from '../lib/format.js';
import AppIcon from './AppIcon.vue';
import DeviceCard from './DeviceCard.vue';
import DeviceRow from './DeviceRow.vue';
import EmptyState from './EmptyState.vue';
import MenuButton from './MenuButton.vue';
import MenuItem from './MenuItem.vue';

const props = defineProps<{
  devices: readonly DirectoryDevice[];
  opening: boolean;
  preferencesBusy?: boolean;
  collapsed: readonly string[];
  refreshedLabel: string;
  removable?: (device: DirectoryDevice) => boolean;
}>();
const emit = defineEmits<{
  select: [id: string];
  open: [device: DirectoryDevice, tool: ConnectionTool];
  customize: [device: DirectoryDevice];
  remove: [device: DirectoryDevice];
  renameGroup: [name: string];
  toggleGroup: [name: string];
}>();
const view = defineModel<DirectoryView>('view', { required: true });
const sort = defineModel<DirectorySort>('sort', { required: true });

const query = ref('');
const filtered = computed(() =>
  sortDevices(
    props.devices.filter((device) => matchesQuery(device, query.value)),
    sort.value,
  ),
);
const groups = computed(() => groupDevices(filtered.value));
// Sections only earn their headers once a person has more than one group.
const sectioned = computed(() => groupDevices(props.devices).length > 1);
const isCollapsed = (name: string) => sectioned.value && props.collapsed.includes(name);
</script>

<template>
  <div class="directory">
    <div class="device-toolbar">
      <label class="search-field">
        <AppIcon name="search" />
        <span class="sr-only">Search devices</span>
        <input v-model="query" type="search" placeholder="Search devices" autocomplete="off" />
      </label>
      <label class="select-field">
        <span class="sr-only">Sort devices</span>
        <select v-model="sort" :disabled="props.preferencesBusy">
          <option value="name">Sort: Name</option>
          <option value="status">Sort: Status</option>
          <option value="seen">Sort: Last seen</option>
        </select>
        <AppIcon name="chevronDown" />
      </label>
      <div class="button-group" role="group" aria-label="Layout">
        <button
          type="button"
          class="icon-button"
          aria-label="Grid"
          :disabled="props.preferencesBusy"
          title="Grid"
          :aria-pressed="view === 'grid' ? 'true' : 'false'"
          @click="view = 'grid'"
        >
          <AppIcon name="grid" />
        </button>
        <button
          type="button"
          class="icon-button"
          aria-label="List"
          :disabled="props.preferencesBusy"
          title="List"
          :aria-pressed="view === 'list' ? 'true' : 'false'"
          @click="view = 'list'"
        >
          <AppIcon name="list" />
        </button>
      </div>
      <span class="device-count">
        <template v-if="filtered.length !== props.devices.length">
          {{ filtered.length }} of {{ props.devices.length }} shown
        </template>
        <template v-else-if="props.refreshedLabel">updated {{ props.refreshedLabel }}</template>
      </span>
    </div>

    <template v-if="filtered.length">
      <section
        v-for="group in groups"
        :key="group.name"
        class="device-group"
        :data-collapsed="isCollapsed(group.name) ? 'true' : 'false'"
      >
        <header v-if="sectioned" class="group-head">
          <button
            type="button"
            class="group-toggle"
            :disabled="props.preferencesBusy"
            :aria-expanded="isCollapsed(group.name) ? 'false' : 'true'"
            @click="emit('toggleGroup', group.name)"
          >
            <AppIcon name="chevronDown" class="group-chevron" />
            <AppIcon name="folder" class="group-icon" />
            <span class="group-name">{{ group.name }}</span>
            <span class="group-count">{{ pluralize(group.devices.length, 'device') }}</span>
          </button>
          <MenuButton :label="`Group actions for ${group.name}`" small>
            <MenuItem
              icon="edit"
              :disabled="props.preferencesBusy"
              @click="emit('renameGroup', group.name)"
              >Rename group…</MenuItem
            >
          </MenuButton>
        </header>
        <div
          v-if="!isCollapsed(group.name)"
          :class="view === 'grid' ? 'device-grid' : 'device-list'"
        >
          <template v-if="view === 'grid'">
            <DeviceCard
              v-for="device in group.devices"
              :key="device.id"
              :device="device"
              :opening="props.opening"
              :show-group="false"
              :preferences-busy="props.preferencesBusy"
              :removable="props.removable?.(device) ?? false"
              @select="emit('select', device.id)"
              @open="emit('open', device, $event)"
              @customize="emit('customize', device)"
              @remove="emit('remove', device)"
            />
          </template>
          <template v-else>
            <DeviceRow
              v-for="device in group.devices"
              :key="device.id"
              :device="device"
              :opening="props.opening"
              :preferences-busy="props.preferencesBusy"
              :removable="props.removable?.(device) ?? false"
              @select="emit('select', device.id)"
              @open="emit('open', device, $event)"
              @customize="emit('customize', device)"
              @remove="emit('remove', device)"
            />
          </template>
        </div>
      </section>
    </template>
    <EmptyState v-else title="No matching devices" icon="search">
      Try a different name, hostname or group.
      <template #actions>
        <button type="button" @click="query = ''">Clear search</button>
      </template>
    </EmptyState>
  </div>
</template>
